import { AccountData, OrderData, SchedulerConfigurationData } from '../../../api';
import { isManuallySeated, pageConfigOf } from '../../utils/orderSeating';
import { calculateRequiredResourceCount } from '../../dialogs/EditOrderDialog/components/resourceSelectionPanel/resourceSelectionUtils';

export enum ResourceValidationErrorType {
  INSUFFICIENT_SPACE = 'insufficient_space',
  WRONG_GROUP = 'wrong_group',
  MASK_NOT_ALLOWED = 'mask_not_allowed',
  GENERIC = 'generic',
}

export interface ResourceValidationResult {
  isValid: boolean;
  errorMessage?: string;
  errorType?: ResourceValidationErrorType;
  warningMessage?: string;
  warningType?: ResourceValidationErrorType;
}

/**
 * Parse a mask string into a boolean array representing allowed positions
 * Mask is stored in reverse order (rightmost bit is position 0)
 */
export const parseMaskToBitArray = (mask: string, resourceCount: number): boolean[] => {
  const paddedMask = mask.padEnd(resourceCount, '0').substring(0, resourceCount);
  const bitArray: boolean[] = [];

  // Reverse the mask since it's stored backwards
  for (let i = resourceCount - 1; i >= 0; i--) {
    bitArray.push(paddedMask[i] === '1');
  }

  return bitArray;
};

/**
 * Validate if there's enough space for the required resources starting from a position
 */
export const validateResourceCapacity = (
  startingResourceIndex: number,
  resourcesNeeded: number,
  config: SchedulerConfigurationData,
): ResourceValidationResult => {
  if (startingResourceIndex + resourcesNeeded > config.resource_count) {
    return {
      isValid: false,
      errorType: ResourceValidationErrorType.INSUFFICIENT_SPACE,
      errorMessage: `Cannot assign to position ${
        startingResourceIndex + 1
      }: need ${resourcesNeeded} adjacent resources but only ${
        config.resource_count - startingResourceIndex
      } available`,
    };
  }

  return { isValid: true };
};

/**
 * Validate if the target resource belongs to the correct scheduler configuration group
 */
export const validateSchedulerConfigMatch = (
  order: OrderData,
  targetSchedulerConfigId: string,
  account: AccountData,
): ResourceValidationResult => {
  // A manually seated order may be placed in any config
  if (isManuallySeated(order)) {
    return { isValid: true };
  }

  const orderConfig = pageConfigOf(order, account);

  if (!orderConfig) {
    return {
      isValid: false,
      errorType: ResourceValidationErrorType.WRONG_GROUP,
      errorMessage: 'No resource configuration found for this order type',
    };
  }

  // If we're trying to place the order in a different config than its original,
  // this is considered a manual override and should be allowed with a warning
  if (orderConfig.id !== targetSchedulerConfigId) {
    // Verify that the target scheduler config exists
    const targetConfig = account?.shared_resources?.scheduler_configs?.find(
      (config) => config.id === targetSchedulerConfigId,
    );
    if (!targetConfig) {
      return {
        isValid: false,
        errorType: ResourceValidationErrorType.WRONG_GROUP,
        errorMessage: 'Target resource configuration not found',
      };
    }

    // Allow manual override to different config but show warning
    return {
      isValid: true,
      warningMessage: 'This order is being moved to a different resource group than originally configured',
      warningType: ResourceValidationErrorType.WRONG_GROUP,
    };
  }

  return { isValid: true };
};

/**
 * Validate if the order page mask allows assignment to specific resource positions
 */
export const validateOrderPageMask = (
  order: OrderData,
  resourceIds: number[],
  config: SchedulerConfigurationData,
  isManualOverride = false,
): ResourceValidationResult => {
  // If this is a manual override or the order is already manually seated, skip mask validation
  if (isManualOverride || isManuallySeated(order)) {
    return { isValid: true };
  }

  // Find the mask for this order page
  const maskData = config.allowed_masks?.find((mask) => mask.order_page_id === order.order_page_id);

  if (!maskData) {
    // Return as invalid but will be converted to warning by parent function
    return {
      isValid: false,
      errorType: ResourceValidationErrorType.MASK_NOT_ALLOWED,
      errorMessage: 'This order type is not normally allowed on any resources in this configuration',
    };
  }

  // Parse the mask to get allowed positions
  const allowedPositions = parseMaskToBitArray(maskData.mask, config.resource_count);

  // Check if all requested resource positions are allowed
  const disallowedPositions = resourceIds.filter((resourceId) => !allowedPositions[resourceId]);

  if (disallowedPositions.length > 0) {
    const disallowedDisplay = disallowedPositions.map((id) => id + 1).join(', ');
    // Return as invalid but will be converted to warning by parent function
    return {
      isValid: false,
      errorType: ResourceValidationErrorType.MASK_NOT_ALLOWED,
      errorMessage: `This order type is not normally allowed on resource positions: ${disallowedDisplay}`,
    };
  }

  return { isValid: true };
};

/**
 * Comprehensive validation for a resource assignment
 */
export const validateResourceAssignment = (
  order: OrderData,
  targetResourceIndex: number,
  targetSchedulerConfigId: string,
  resourceIndexes: number[],
  account: AccountData,
): ResourceValidationResult => {
  const warnings: string[] = [];
  let warningType: ResourceValidationErrorType | undefined;

  // 1. Validate scheduler config match (check for cross-group warnings)
  const configValidation = validateSchedulerConfigMatch(order, targetSchedulerConfigId, account);
  if (!configValidation.isValid) {
    return configValidation;
  }

  // Collect warnings from config validation
  if (configValidation.warningMessage) {
    warnings.push(configValidation.warningMessage);
    warningType = configValidation.warningType;
  }

  // Get target config for validation (not current order config)
  const targetConfig = account?.shared_resources?.scheduler_configs?.find(
    (config) => config.id === targetSchedulerConfigId,
  );
  if (!targetConfig) {
    return {
      isValid: false,
      errorType: ResourceValidationErrorType.GENERIC,
      errorMessage: 'Unable to find target resource configuration',
    };
  }

  // Check if this is a manual override (different from original config)
  const originalConfig = account?.shared_resources?.scheduler_configs?.find((config) =>
    config.allowed_masks?.some((mask) => mask.order_page_id === order.order_page_id),
  );
  const isManualOverride = originalConfig?.id !== targetSchedulerConfigId || isManuallySeated(order);

  // 2. Validate capacity (not enough space) - this still blocks
  const requiredResourceCount = calculateRequiredResourceCount(
    order.persons || 1,
    targetConfig.persons_per_resource || 1,
  );
  const capacityValidation = validateResourceCapacity(targetResourceIndex, requiredResourceCount, targetConfig);
  if (!capacityValidation.isValid) {
    return capacityValidation;
  }

  // 3. Check mask permissions but only warn, don't block
  const maskValidation = validateOrderPageMask(order, resourceIndexes, targetConfig, isManualOverride);
  if (!maskValidation.isValid && maskValidation.errorMessage) {
    warnings.push(maskValidation.errorMessage);
    warningType = warningType || maskValidation.errorType;
  }

  // Return valid with collected warnings
  return {
    isValid: true,
    warningMessage: warnings.length > 0 ? warnings.join('. ') : undefined,
    warningType,
  };
};

/**
 * Get available resource positions for an order based on mask restrictions
 */
export const getAvailableResourcesForOrder = (
  order: OrderData,
  account: AccountData,
): { schedulerConfigId: string; availableResourceIds: number[] } | null => {
  const config = pageConfigOf(order, account);
  if (!config) {
    return null;
  }

  const maskData = config.allowed_masks?.find((mask) => mask.order_page_id === order.order_page_id);
  if (!maskData) {
    return { schedulerConfigId: config.id, availableResourceIds: [] };
  }

  const allowedPositions = parseMaskToBitArray(maskData.mask, config.resource_count);
  const availableResourceIds = allowedPositions
    .map((isAllowed, index) => (isAllowed ? index : -1))
    .filter((index) => index !== -1);

  return {
    schedulerConfigId: config.id,
    availableResourceIds,
  };
};
